import type { CueScript } from "../types";
import { sanitizeScript, plainTextToHtml } from "../core/script";
export function subtitleText(text: string): string {
  return text
    .replace(/^\uFEFF/, "")
    .split(/\r?\n\s*\r?\n/)
    .map((block) =>
      block
        .split(/\r?\n/)
        .filter(
          (line) =>
            !/^WEBVTT(?:\s|$)/i.test(line) &&
            !/^\d+$/.test(line.trim()) &&
            !/-->/.test(line) &&
            !/^NOTE(?:\s|$)/.test(line),
        )
        .join(" "),
    )
    .filter(Boolean)
    .join("\n\n")
    .replace(/<[^>]+>/g, "");
}
export function normalizeScript(
  value: unknown,
  newIdentity = false,
): CueScript | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const x = value as Record<string, unknown>;
  if (
    typeof x.html !== "string" ||
    x.html.length > 400000 ||
    new TextEncoder().encode(x.html).length > 600000
  )
    return null;
  const html = sanitizeScript(x.html);
  if (html.length > 400000 || new TextEncoder().encode(html).length > 600000)
    return null;
  return {
    id:
      !newIdentity && typeof x.id === "string" && x.id.length < 200
        ? x.id
        : crypto.randomUUID(),
    title:
      typeof x.title === "string" && x.title
        ? x.title.slice(0, 200)
        : "Untitled script",
    html,
    updatedAt:
      typeof x.updatedAt === "number" &&
      Number.isFinite(x.updatedAt) &&
      x.updatedAt >= 0 &&
      x.updatedAt <= 1e16
        ? x.updatedAt
        : Date.now(),
    tags: Array.isArray(x.tags)
      ? x.tags
          .filter((t) => typeof t === "string")
          .map((t) => t.slice(0, 80))
          .slice(0, 30)
      : [],
    notes: typeof x.notes === "string" ? x.notes.slice(0, 20000) : "",
  };
}
export async function importScriptFile(file: File): Promise<CueScript> {
  if (file.size > 5 * 1024 * 1024)
    throw new Error("Scripts must be smaller than 5 MB.");
  let html: string;
  const ext = file.name.split(".").pop()?.toLowerCase();
  let source: Record<string, unknown> = {};
  if (ext === "docx") {
    const mammoth = await import("mammoth");
    html = (
      await mammoth.convertToHtml({ arrayBuffer: await file.arrayBuffer() })
    ).value;
  } else if (ext === "html" || ext === "htm") html = await file.text();
  else if (ext === "md" || ext === "markdown") {
    const { marked } = await import("marked");
    html = await marked.parse(await file.text());
  } else if (ext === "json") {
    source = JSON.parse(await file.text());
    if (typeof source.html !== "string")
      throw new Error("Use Restore backup for full workspace backups.");
    html = source.html;
  } else if (ext === "srt" || ext === "vtt")
    html = plainTextToHtml(subtitleText(await file.text()));
  else html = plainTextToHtml(await file.text());
  const script = normalizeScript({
    ...source,
    id: crypto.randomUUID(),
    title: source.title || file.name.replace(/\.[^.]+$/, ""),
    html,
    updatedAt: Date.now(),
    tags: ["Imported"],
  });
  if (!script)
    throw new Error(
      "This script is too large for live prompting. Split it into smaller scripts in a rundown (maximum 400,000 characters and 600 KB of rich text per script).",
    );
  return script;
}
